import {
  FormComboBox,
  FormController,
  FormInput,
  FormSelect,
  FormTextArea,
} from '@/components/form';
import { useAppSelector } from '@/state';
import { ProjectsApi, SubmittalsApi } from '@/state/api';
import { RfiSubmittalsType } from '@/types';
import { submittalStatus, submittalType } from '@/types/enums';
import { dateInputToIso, toDateInputValue } from '@/utils/dateFields';
import { Box, Flex, Separator } from '@chakra-ui/react';
import { OverlayDialog, toasterApi } from '@ui';
import { useMemo } from 'react';
import {
  Control,
  FieldErrors,
  UseFormRegister,
  useWatch,
} from 'react-hook-form';
import z from 'zod';
import {
  flattenSubmittalHierarchy,
  getDescendantIds,
} from '../utils/submittalHierarchy';

type ManageCardProps = {
  overlayId: string;
  isNew?: boolean;
  defaultSubmittalData?:
    RfiSubmittalsType.Submittals.GetSubmittal['response']['data'] | null;
};

const normalizeMetadataForForm = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
};

const ManageCard = ({
  defaultSubmittalData,
  isNew = false,
  overlayId,
}: ManageCardProps) => {
  const [postSubmittalsMutation] =
    SubmittalsApi.Api.usePostSubmittalsMutation();

  const [patchSubmittalMutation] =
    SubmittalsApi.Api.usePatchSubmittalMutation();

  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );

  const { data: getAllProjects } = ProjectsApi.Api.useGetAllProjectsQuery();

  const postSchema = RfiSubmittalsType.Submittals.post.body;
  const patchSchema = RfiSubmittalsType.Submittals.patch.body;
  type PostFormType = z.infer<typeof postSchema>;
  type PatchFormType = z.infer<typeof patchSchema>;

  const formSchema = isNew ? postSchema : patchSchema;
  type FormSchemaType = PostFormType | PatchFormType;

  const handleSubmitSubmittal = async (formData: FormSchemaType) => {
    console.groupCollapsed(
      `[Submittal ${isNew ? 'CREATE' : 'UPDATE'}] submit start`,
    );
    console.log('isNew:', isNew);
    console.log('defaultSubmittalData:', defaultSubmittalData);
    console.log('raw formData:', formData);

    const { submit_by, due_at, submitted_at, returned_at, metadata, ...rest } =
      formData as FormSchemaType & {
        submit_by?: string | null;
        due_at?: string | null;
        submitted_at?: string | null;
        returned_at?: string | null;
        metadata?: string | null;
      };

    console.log('extracted fields:', {
      submit_by,
      due_at,
      submitted_at,
      returned_at,
      metadata,
      rest,
    });

    let normalizedMetadata: any = undefined;
    const metadataTrimmed = typeof metadata === 'string' ? metadata.trim() : '';

    if (metadataTrimmed) {
      try {
        normalizedMetadata = JSON.parse(metadataTrimmed);
        console.log('metadata parsed as JSON object:', normalizedMetadata);
      } catch {
        normalizedMetadata = metadataTrimmed;
        console.log('metadata kept as plain string:', normalizedMetadata);
      }
    } else {
      console.log('metadata blank -> omitted from payload');
    }

    const payload: FormSchemaType = {
      ...rest,
      ...(normalizedMetadata !== undefined
        ? { metadata: normalizedMetadata }
        : {}),

      // submit_by is a date-only column, the other three are timestamps.
      submit_by: (toDateInputValue(submit_by) || null) as any,
      due_at: dateInputToIso(due_at) as any,
      submitted_at: dateInputToIso(submitted_at) as any,
      returned_at: dateInputToIso(returned_at) as any,
    };

    console.log('final payload before mutation:', payload);

    try {
      if (isNew) {
        console.log('calling postSubmittalsMutation with payload');
        const response = await postSubmittalsMutation({
          body: payload as PostFormType,
        }).unwrap();

        console.log('create success response:', response);

        toasterApi({
          type: 'success',
          status: 201,
          code: 'Success',
          message: 'Submittal created successfully',
        });
      } else {
        console.log('calling patchSubmittalMutation with payload + params', {
          submittal_id: defaultSubmittalData?.id,
          payload,
        });

        const response = await patchSubmittalMutation({
          params: {
            submittal_id: defaultSubmittalData?.id as number,
          },
          body: payload,
        }).unwrap();

        console.log('update success response:', response);

        toasterApi({
          type: 'success',
          status: 201,
          code: 'Success',
          message: 'Submittal updated successfully',
        });
      }

      console.log('closing overlay:', overlayId);
      OverlayDialog.close(overlayId);
    } catch (error: any) {
      console.error(
        `[Submittal ${isNew ? 'CREATE' : 'UPDATE'}] mutation failed`,
        error,
      );

      console.error('full backend error object:', {
        error,
        status: error?.status,
        data: error?.data,
        message: error?.message,
        originalStatus: error?.originalStatus,
      });

      if (error?.data?.errors) {
        console.error('backend field validation errors:', error.data.errors);
        Object.entries(error.data.errors).forEach(([field, messages]) => {
          console.error(`Field "${field}" errors:`, messages);
        });
      }

      if (error?.data?.detail) {
        console.error('backend detail:', error.data.detail);
      }

      if (error?.data?.message) {
        console.error('backend message:', error.data.message);
      }

      const backendMessage = error?.data?.errors
        ? Object.entries(error.data.errors)
            .map(([field, messages]) => {
              const msg = Array.isArray(messages)
                ? messages.join(', ')
                : String(messages);
              return `${field}: ${msg}`;
            })
            .join(' | ')
        : error?.data?.detail || error?.data?.message || error?.message;

      toasterApi({
        type: 'error',
        status: error?.status || 500,
        code: error?.data?.code || 'Error',
        message:
          backendMessage ||
          `Failed to ${isNew ? 'create' : 'update'} submittal`,
      });
    } finally {
      console.groupEnd();
    }
  };

  return (
    <Box
      width={{ base: 'calc(100vw - 4rem)', lg: 'min(960px, 72vw)' }}
      maxW="960px"
      mx="auto"
      px={{ base: 0, md: 4 }}
    >
      <FormController
        key={`submittal-form-${isNew}-${defaultSubmittalData?.id}`}
        size="lg"
        formSchema={formSchema}
        defaultValues={{
          project:
            (isNew
              ? (selectedProject?.id as number)
              : defaultSubmittalData?.project) ??
            (selectedProject?.id as number),
          parent: isNew ? null : (defaultSubmittalData?.parent ?? null),
          submit_by: isNew
            ? ''
            : toDateInputValue(defaultSubmittalData?.submit_by),
          due_at: isNew ? '' : toDateInputValue(defaultSubmittalData?.due_at),
          number: (isNew ? '' : (defaultSubmittalData as any)?.number) ?? '',
          returned_at: isNew
            ? ''
            : toDateInputValue(defaultSubmittalData?.returned_at),
          status: (isNew ? 'draft' : defaultSubmittalData?.status) ?? 'draft',
          submittal_type:
            (isNew ? 'shop_drawing' : defaultSubmittalData?.submittal_type) ??
            'shop_drawing',
          zone_level: (isNew ? '' : defaultSubmittalData?.zone_level) ?? '',
          system: (isNew ? '' : defaultSubmittalData?.system) ?? '',
          vendor: (isNew ? '' : defaultSubmittalData?.vendor) ?? '',
          drawing_refs: (isNew ? '' : defaultSubmittalData?.drawing_refs) ?? '',
          final_response:
            (isNew ? '' : defaultSubmittalData?.final_response) ?? '',
          submitted_at: isNew
            ? ''
            : toDateInputValue(defaultSubmittalData?.submitted_at),
          title: isNew ? '' : defaultSubmittalData?.title,
          description: (isNew ? '' : defaultSubmittalData?.description) ?? '',
          revision: (isNew ? 0 : defaultSubmittalData?.revision) ?? 0,
          spec_section: (isNew ? '' : defaultSubmittalData?.spec_section) ?? '',
          metadata: isNew
            ? ''
            : normalizeMetadataForForm(defaultSubmittalData?.metadata),

          specification_title:
            (isNew ? '' : defaultSubmittalData?.specification_title) ?? '',
          spec_ref: (isNew ? '' : defaultSubmittalData?.spec_ref) ?? '',
          attn_of: (isNew ? '' : defaultSubmittalData?.attn_of) ?? '',

          export_to_name:
            (isNew ? '' : defaultSubmittalData?.export_to_name) ?? '',
          export_to_address:
            (isNew ? '' : defaultSubmittalData?.export_to_address) ?? '',
          export_items_shipped_via:
            (isNew ? '' : defaultSubmittalData?.export_items_shipped_via) ?? '',
          export_these_items_submitted:
            (isNew
              ? 'out_for_approval'
              : defaultSubmittalData?.export_these_items_submitted) ??
            'out_for_approval',
          ...(!isNew
            ? {
                ball_in_court:
                  (defaultSubmittalData as any)?.ball_in_court ?? '',
                package: defaultSubmittalData?.package ?? '',
                discipline: defaultSubmittalData?.discipline ?? '',
                export_attention: defaultSubmittalData?.export_attention ?? '',
                export_items_shipped_other:
                  defaultSubmittalData?.export_items_shipped_other ?? '',
              }
            : {}),
        }}
        onFormSubmit={handleSubmitSubmittal}
      >
        {({
          register,
          control,
          errors,
        }: {
          register: UseFormRegister<FormSchemaType>;
          control: Control<FormSchemaType>;
          errors: FieldErrors<FormSchemaType>;
        }) => {
          console.log('FormController validation errors:', errors);

          const projectId = useWatch({
            control,
            name: 'project',
          });
          const { data: allSubmittals, isLoading: isLoadingSubmittals } =
            SubmittalsApi.Api.useGetAllSubmittalsQuery(
              { query: { project: Number(projectId) } },
              { skip: !projectId },
            );

          const parentOptions = useMemo(() => {
            const submittals = allSubmittals?.data ?? [];
            const currentId = defaultSubmittalData?.id;
            const excludedIds =
              currentId == null
                ? new Set<number>()
                : getDescendantIds(submittals, currentId);

            if (currentId != null) excludedIds.add(currentId);

            const selectableSubmittals = submittals.filter(
              (submittal) => !excludedIds.has(submittal.id),
            );

            return flattenSubmittalHierarchy(selectableSubmittals).map(
              (submittal) => ({
                id: submittal.id,
                display_name: `${submittal._depth > 0 ? `${'↳ '.repeat(submittal._depth)}` : ''}${submittal.number} — ${submittal.title} · ${
                  submittalStatus.find(
                    (status) => status.value === submittal.status,
                  )?.label ?? submittal.status
                }`,
              }),
            );
          }, [allSubmittals?.data, defaultSubmittalData?.id]);

          return (
            <Flex
              flexDirection="column"
              gapY={6}
              width="100%"
              maxW="960px"
              mx="auto"
            >
              <FormInput
                register={register}
                control={control}
                errors={errors}
                id="title"
                label="Title"
                showLabelAtTop
                required
              />

              <FormTextArea
                register={register}
                control={control}
                errors={errors}
                id="description"
                label="Description"
                showLabelAtTop
              />

              <Flex gapX={4}>
                <FormInput
                  register={register}
                  control={control}
                  errors={errors}
                  id="revision"
                  label="Revision"
                  min={0}
                  type="number"
                  showLabelAtTop
                />
                <FormInput
                  register={register}
                  control={control}
                  errors={errors}
                  id="spec_section"
                  label="Spec Section"
                  showLabelAtTop
                />
              </Flex>

              <Flex gapX={4}>
                <FormComboBox
                  register={register}
                  control={control}
                  errors={errors}
                  id="project"
                  label="Project"
                  keyForDisplay="name"
                  keyForValue="id"
                  items={getAllProjects ?? []}
                  showLabelAtTop
                  required
                />
                <FormComboBox
                  register={register}
                  control={control}
                  errors={errors}
                  id="parent"
                  label="Parent Submittal"
                  keyForDisplay="display_name"
                  keyForValue="id"
                  items={parentOptions}
                  isLoading={isLoadingSubmittals}
                  clearable
                  helperText="Optional. Closed and active submittals can both be selected as parents."
                  showLabelAtTop
                />
              </Flex>

              <FormInput
                register={register}
                control={control}
                errors={errors}
                id="zone_level"
                label="Zone Level"
                placeholder="e.g. L5 East"
                showLabelAtTop
                required
              />

              <FormInput
                register={register}
                control={control}
                errors={errors}
                id="number"
                label="Submittal #"
                placeholder="e.g. SUB-003 or S-12"
                showLabelAtTop
                required={isNew}
              />

              <Flex gapX={4}>
                <FormSelect
                  register={register}
                  control={control}
                  errors={errors}
                  id="status"
                  label="Status"
                  keyForDisplay="label"
                  keyForValue="value"
                  items={submittalStatus}
                  showLabelAtTop
                />
                <FormSelect
                  register={register}
                  control={control}
                  errors={errors}
                  id="submittal_type"
                  label="Submittal Type"
                  keyForDisplay="label"
                  keyForValue="value"
                  items={submittalType}
                  showLabelAtTop
                />
                <FormInput
                  register={register}
                  control={control}
                  errors={errors}
                  id="system"
                  label="System"
                  showLabelAtTop
                />
              </Flex>

              <Flex gapX={4}>
                <FormInput
                  register={register}
                  control={control}
                  errors={errors}
                  id="due_at"
                  label="Due At"
                  showLabelAtTop
                  type="date"
                  required={isNew}
                />
                <FormInput
                  register={register}
                  control={control}
                  errors={errors}
                  id="submitted_at"
                  label="Submitted Date"
                  showLabelAtTop
                  type="date"
                />
                <FormInput
                  register={register}
                  control={control}
                  errors={errors}
                  id="returned_at"
                  label="Returned Date"
                  showLabelAtTop
                  type="date"
                />
              </Flex>

              <Flex gapX={4}>
                <FormInput
                  register={register}
                  control={control}
                  errors={errors}
                  id="vendor"
                  label="Vendor (Manufacturer)"
                  showLabelAtTop
                />
                <FormInput
                  register={register}
                  control={control}
                  errors={errors}
                  id="drawing_refs"
                  label="Drawing Refs"
                  showLabelAtTop
                />
              </Flex>

              {!isNew && (
                <Flex gapX={4}>
                  <FormInput
                    register={register}
                    control={control}
                    errors={errors}
                    id="discipline"
                    label="Discipline"
                    showLabelAtTop
                  />
                  <FormInput
                    register={register}
                    control={control}
                    errors={errors}
                    id="ball_in_court"
                    label="Ball in Court"
                    placeholder="e.g. GC / Architect / Procore / John Smith"
                    showLabelAtTop
                  />
                  <FormInput
                    register={register}
                    control={control}
                    errors={errors}
                    id="package"
                    label="Package"
                    showLabelAtTop
                  />
                </Flex>
              )}

              <Separator />

              <FormInput
                register={register}
                control={control}
                errors={errors}
                id="specification_title"
                label="Specification Title"
                showLabelAtTop
              />
              <Flex gapX={4}>
                <FormInput
                  register={register}
                  control={control}
                  errors={errors}
                  id="spec_ref"
                  label="Spec Ref"
                  showLabelAtTop
                />
                <FormInput
                  register={register}
                  control={control}
                  errors={errors}
                  id="attn_of"
                  label="Attn. Of"
                  showLabelAtTop
                />
              </Flex>

              <Separator />

              <FormInput
                register={register}
                control={control}
                errors={errors}
                id="export_to_name"
                label="Default Export To Name"
                showLabelAtTop
              />
              <FormTextArea
                register={register}
                control={control}
                errors={errors}
                id="export_to_address"
                label="Default Export To Address"
                showLabelAtTop
              />
              {!isNew && (
                <FormInput
                  register={register}
                  control={control}
                  errors={errors}
                  id="export_attention"
                  label="Default Export Attention"
                  showLabelAtTop
                />
              )}

              <Flex gapX={4}>
                <FormSelect
                  register={register}
                  control={control}
                  errors={errors}
                  id="export_items_shipped_via"
                  label="Default Items Shipped Via"
                  keyForDisplay="label"
                  keyForValue="value"
                  items={[
                    { label: 'US Mail', value: 'us_mail' },
                    { label: 'Overnight', value: 'overnight' },
                    { label: 'Courier', value: 'courier' },
                    { label: 'Karas Delivered', value: 'karas_delivered' },
                    { label: 'Ground', value: 'ground' },
                    { label: 'Uploaded to Procore', value: 'uploaded_procore' },
                    { label: 'Email', value: 'email' },
                    { label: 'Other', value: 'other' },
                  ]}
                  showLabelAtTop
                />
                {!isNew && (
                  <FormInput
                    register={register}
                    control={control}
                    errors={errors}
                    id="export_items_shipped_other"
                    label="Default Shipping Other"
                    showLabelAtTop
                  />
                )}
              </Flex>

              <FormSelect
                register={register}
                control={control}
                errors={errors}
                id="export_these_items_submitted"
                label="Default These Items Submitted As"
                keyForDisplay="label"
                keyForValue="value"
                items={[
                  { label: 'For reference only', value: 'for_reference_only' },
                  {
                    label: 'For information only',
                    value: 'for_information_only',
                  },
                  {
                    label: 'For review & comment',
                    value: 'for_review_comment',
                  },
                  { label: 'Out for approval', value: 'out_for_approval' },
                  {
                    label: 'For final approval',
                    value: 'for_final_approval',
                  },
                  {
                    label: 'For record/distribution',
                    value: 'for_record_distribution',
                  },
                ]}
                showLabelAtTop
              />
            </Flex>
          );
        }}
      </FormController>
    </Box>
  );
};

export default ManageCard;
